import { useMemo, useState } from 'react';
import { ApiError } from '../../lib/api';
import { usePublicCatalog, useSubmitInquiry, type PublicCatalogAsset } from './api';

function money(n: number, currency: string) {
  try {
    return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
  } catch {
    return `${n} ${currency}`;
  }
}

export default function RentalCatalogPage() {
  const { data, isLoading, error } = usePublicCatalog();
  const submit = useSubmitInquiry();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [form, setForm] = useState({ customerName: '', email: '', phone: '', message: '', startDate: '', endDate: '', website: '' });
  const [sent, setSent] = useState(false);

  const grouped = useMemo(() => {
    const map = new Map<string, PublicCatalogAsset[]>();
    for (const a of data?.assets ?? []) {
      const k = a.category || 'Ostatní';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(a);
    }
    return [...map.entries()];
  }, [data]);

  const toggle = (code: string) => {
    setSelected((p) => {
      const n = new Set(p);
      if (n.has(code)) n.delete(code); else n.add(code);
      return n;
    });
  };
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customerName.trim()) return;
    submit.mutate(
      {
        customerName: form.customerName.trim(),
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        message: form.message.trim() || undefined,
        startDate: form.startDate || undefined,
        endDate: form.endDate || undefined,
        assetCodes: [...selected],
        website: form.website,
      },
      { onSuccess: () => setSent(true) },
    );
  };

  const input = 'h-10 w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 text-sm';

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100">
      <div className="mx-auto max-w-4xl px-4 py-10">
        {isLoading && <p className="text-sm text-zinc-500">Načítám katalog…</p>}
        {error && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-10 text-center">
            <h1 className="text-lg font-semibold mb-1">Katalog není dostupný</h1>
            <p className="text-sm text-zinc-500">{(error as ApiError).message}</p>
          </div>
        )}

        {data && (
          <>
            <header className="mb-8">
              <p className="text-xs uppercase tracking-wide text-zinc-400">{data.studioName}</p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight">Půjčovna — katalog</h1>
              <p className="mt-1 text-sm text-zinc-500">Vyber, co tě zajímá, a pošli nezávaznou poptávku. Ceny jsou orientační, za 1 den pronájmu.</p>
            </header>

            {grouped.length === 0 ? (
              <p className="text-sm text-zinc-400">Katalog je momentálně prázdný.</p>
            ) : (
              <div className="space-y-8">
                {grouped.map(([cat, assets]) => (
                  <section key={cat}>
                    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-zinc-500">{cat}</h2>
                    <div className="grid gap-3 sm:grid-cols-2">
                      {assets.map((a) => {
                        const on = selected.has(a.code);
                        return (
                          <button
                            type="button"
                            key={a.code}
                            onClick={() => toggle(a.code)}
                            className={`flex flex-col rounded-lg border p-4 text-left transition-colors ${on ? 'border-blue-400 dark:border-blue-600 bg-blue-50/60 dark:bg-blue-950/30' : 'border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 hover:border-zinc-300 dark:hover:border-zinc-700'}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <span className="font-medium">{a.name}</span>
                              <span className={`mt-0.5 h-4 w-4 shrink-0 rounded border ${on ? 'border-blue-500 bg-blue-500 text-white' : 'border-zinc-300 dark:border-zinc-600'} flex items-center justify-center text-[10px]`}>{on ? '✓' : ''}</span>
                            </div>
                            {a.description && <span className="mt-0.5 text-xs text-zinc-500 line-clamp-2">{a.description}</span>}
                            <div className="mt-2 flex items-baseline gap-2">
                              <span className="text-lg font-bold tabular-nums">{money(a.dailyRate, a.currency)}</span>
                              <span className="text-xs text-zinc-500">/ den</span>
                              {a.weeklyRate && <span className="text-xs text-zinc-400">· {money(a.weeklyRate, a.currency)}/týden</span>}
                            </div>
                            {a.deposit > 0 && <span className="mt-0.5 text-xs text-zinc-400">kauce {money(a.deposit, a.currency)}</span>}
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}

            {/* Poptávkový formulář */}
            <section className="mt-10 rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5">
              <h2 className="mb-1 text-lg font-semibold">Nezávazná poptávka</h2>
              <p className="mb-4 text-sm text-zinc-500">
                {selected.size > 0 ? `Vybráno ${selected.size} ${selected.size === 1 ? 'položka' : selected.size < 5 ? 'položky' : 'položek'}.` : 'Můžeš vybrat položky výše, nebo jen napsat, co potřebuješ.'}
              </p>

              {sent ? (
                <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50/60 dark:bg-emerald-950/20 p-6 text-center">
                  <p className="text-2xl">✅</p>
                  <p className="mt-1 font-semibold text-emerald-800 dark:text-emerald-300">Poptávka odeslána</p>
                  <p className="text-sm text-emerald-700/80 dark:text-emerald-400/70">Ozveme se ti co nejdřív. Děkujeme!</p>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="space-y-3">
                  <input className="hidden" tabIndex={-1} autoComplete="off" value={form.website} onChange={set('website')} aria-hidden />
                  <div className="grid gap-3 sm:grid-cols-3">
                    <input className={input} placeholder="Jméno / produkce *" value={form.customerName} onChange={set('customerName')} required />
                    <input className={input} type="email" placeholder="E-mail" value={form.email} onChange={set('email')} />
                    <input className={input} placeholder="Telefon" value={form.phone} onChange={set('phone')} />
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="text-xs text-zinc-500">Od <input className={input} type="date" value={form.startDate} onChange={set('startDate')} /></label>
                    <label className="text-xs text-zinc-500">Do <input className={input} type="date" value={form.endDate} onChange={set('endDate')} /></label>
                  </div>
                  <textarea className="w-full rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 px-3 py-2 text-sm" rows={3} placeholder="Zpráva — k čemu to potřebuješ, počty kusů…" value={form.message} onChange={set('message')} />
                  {submit.error && <p className="text-sm text-red-600 dark:text-red-400">{(submit.error as ApiError).message}</p>}
                  <button type="submit" disabled={submit.isPending || !form.customerName.trim()} className="h-10 rounded-md bg-zinc-900 px-5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white">
                    {submit.isPending ? 'Odesílám…' : 'Odeslat poptávku'}
                  </button>
                </form>
              )}

              {(data.studioEmail || data.studioPhone) && (
                <p className="mt-4 text-xs text-zinc-400">
                  Nebo přímo: {data.studioEmail && <span>{data.studioEmail} </span>}{data.studioPhone && <span>· {data.studioPhone}</span>}
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
